// storage/CapacitorCache.ts
import { ConfigureOptions, MigrateResult, Preferences, PreferencesPlugin } from '@capacitor/preferences';
import { isCapacitorNative } from './Constants';
import { AsyncStorage } from 'oidc-client-ts';

class AsyncLocalStorage implements PreferencesPlugin {

  configure(options: ConfigureOptions): Promise<void> {
    throw new Error('Method not implemented.');
  }
  migrate(): Promise<MigrateResult> {
    throw new Error('Method not implemented.');
  }
  removeOld(): Promise<void> {
    throw new Error('Method not implemented.');
  }

  get = async (options: { key: string }) => {
    const value = localStorage.getItem(options.key);
    return { value };
  };
  set = async (options: { key: string, value: string }) => {
    localStorage.setItem(options.key, options.value);
    return Promise.resolve();
  };

  remove = async (options: { key: string }) => {
    localStorage.removeItem(options.key);
  };
  clear = async () => {
    localStorage.clear();
  };
  keys =  async () => {
    return { keys: Object.keys(localStorage) };
  }
}

export class CapacitorCache implements AsyncStorage {
  private store: Record<string, string | null> = {};
  private userId: string | undefined = undefined; // userId for which the cache is initialized
  private driver = isCapacitorNative() ? Preferences : new AsyncLocalStorage();

  constructor(userId?: string) {
    this.userId = userId;
  }

  async init(userId?: string) {
    if(userId) {
      this.userId = userId;
    }
    const { keys } = await this.driver.keys();
    // console.log("Initializing CapacitorCache with user id:", userId, "keys:", keys);
    await Promise.all(
      keys.map(async k => {
        if(userId) {
          k = `${userId}-${k}`;
        } else if(this.userId) {
          k = `${this.userId}-${k}`;
        }
        const { value } = await this.driver.get({ key: k });
        this.store[k] = value ?? null;
      })
    );
  }

  getItem   = async (k: string) => {
    // console.log("getItem", k);
    if(this.userId) {
      k = `${this.userId}-${k}`;
    }
    if(this.store[k]) {
      // console.log("Cache hit for key:", k, this.store[k]);
      return Promise.resolve(this.store[k]);
    } else {
      const value = await this.driver.get({ key: k }).then(result => {
        this.store[k] = result.value;
        return result.value;
      });
      return value
    }
  }
  setItem   = (k: string, v: string) => {
    if(this.userId) {
      k = `${this.userId}-${k}`;
    }
    this.store[k] = v;
    return this.driver.set({ key: k, value: v }).catch(console.error);
  };
  removeItem = (k: string) => {
    if(this.userId) {
      k = `${this.userId}-${k}`;
    }
    delete this.store[k];
    return this.driver.remove({ key: k }).catch(console.error);
  };
  clear = () => {
    this.store = {};
    return this.driver.clear().catch(console.error);
  };
  key = (i: number) => Promise.resolve(Object.keys(this.store)[i] ?? null);
  get length() { return Promise.resolve(Object.keys(this.store).length); }
}

export default new CapacitorCache();

export function makeAsyncAdapter(cache: CapacitorCache) {
  return {
    getItem:    (k: string) => Promise.resolve(cache.getItem(k)),
    setItem:    (k: string, v: string) => { cache.setItem(k, v); return Promise.resolve(); },
    removeItem: (k: string) => { cache.removeItem(k); return Promise.resolve(); }
  } as const;
}